import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import "./orderDetail.scss";
import { Ring } from "@uiball/loaders";
import { NavLink, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { useSelector } from "react-redux";
import useOrder from "hooks/useOrder";
import { useListOrderReviews } from "hooks/useReviews";
import { canManageReviews } from "utils/permissions";
import UserNote from "components/Ui/UserNote/UserNote";
import Receipt from "../Receipt";
import { useReactToPrint } from "react-to-print";
import { buildOrderClipboardText, copyTextToClipboard } from "util/orderClipboard";
import { formatMoney } from "utils/money";
import { formatDuration } from "utils/orderTiming";
import {
    Field,
    StatusPill,
    OrderTimeline,
    CustomerTimeline,
    DriverCard,
    OrderMoney,
} from "components/Ui/OrderDetail/OrderDetailParts";

const REVIEW_TARGET_LABELS = {
    product: "منتج",
    vendor: "مطعم",
    delivery: "سائق",
};

const OrderStars = ({ value }) => {
    const filled = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
    return (
        <span style={{ color: "#f5a623" }}>
            {[1, 2, 3, 4, 5].map((i) => (
                <FontAwesomeIcon key={i} icon={i <= filled ? faStarSolid : faStarRegular} />
            ))}
        </span>
    );
};

const reviewTargetLabel = (review) => {
    const type = REVIEW_TARGET_LABELS[review.targetType] ?? review.targetType;
    const name = review.target?.name;
    if (!name) return `${type} #${review.targetId}`;
    if (review.targetType === "product" && review.target?.vendorName) {
        return `${type}: ${name} (${review.target.vendorName})`;
    }
    return `${type}: ${name}`;
};

const OrderDetails = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const canEditNotes = !!(adminInfo?.super_admin || adminInfo?.roles?.manage_users);
    const { loading, fetchOrderBYId } = useOrder();
    const [order, setOrder] = useState(null);
    const [date, setDate] = useState(new Date());
    const { id } = useParams();
    const componentRef = useRef(null);
    // Only a user who may manage reviews. For the others the API refused the
    // call, and this page then said «لا توجد تقييمات على هذا الطلب», which
    // was not true: it did not know.
    const canReadReviews = canManageReviews(adminInfo);
    const { reviews: orderReviews, loading: reviewsLoading } = useListOrderReviews(id, {
        enabled: canReadReviews,
    });
    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await fetchOrderBYId(id);
                setOrder(response);
                const newDate = new Date(response.createdAt);
                // newDate.setHours(newDate.getHours() + 1); // Add 1 hour
                setDate(newDate);
            } catch (error) {
                console.log(error);
            }
        };
        fetchOrder();
    }, [id]);

    const handlePrint = useReactToPrint({
        content: () => {
            const printableComponent = componentRef.current;

            if (printableComponent) {
                printableComponent.classList.remove("hidden");
            }
            return printableComponent;
        },
        onAfterPrint: () => {
            const printableComponent = componentRef.current;
            if (printableComponent) {
                printableComponent.classList.add("hidden");
            }
        },
    });

    /**
     * Copies a concise vendor-facing Arabic summary for quick sharing.
     */
    const handleCopyOrderData = async () => {
        if (!order) {
            return;
        }

        const orderDataAsText = buildOrderClipboardText(order);

        try {
            await copyTextToClipboard(orderDataAsText);
            props.showMessage?.("success", "تم النسخ", "تم نسخ بيانات الطلب");
        } catch {
            props.showMessage?.("error", "فشل النسخ", "تعذر نسخ بيانات الطلب");
        }
    };
    const products = order?.cart_products ?? [];

    return (
        <>
            <div className="add-product order-page">
                {loading ? (
                    <div className="loading-spinner center">
                        <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                    </div>
                ) : null}

                <div className="order-page__head">
                    <div className="order-page__title">
                        <h3>الطلب رقم {order?.id}</h3>
                        <StatusPill status={order?.status} />
                    </div>
                    <div className="order-page__actions">
                        <button onClick={handlePrint}>🖨️ طباعة الإيصال</button>
                        <button onClick={handleCopyOrderData}>📋 نسخ بيانات الطلب</button>
                    </div>
                </div>

                {/* THE FOUR NUMBERS SOMEBODY OPENS THIS PAGE FOR. They were four
                 * label:value rows in the middle of thirty others, at the same
                 * weight as the customer's second phone number. */}
                <OrderMoney order={order} createdAt={date} />

                {/* THREE PARTIES, THREE CARDS. It was one flat wall of rows in
                 * unequal columns, so the short columns ended early and left a
                 * hole the width of the page — visible in dark mode as a black
                 * void beside the customer's details. */}
                <div className="order-cards">
                    <section className="surface-card order-card">
                        <h4 className="order-card__title">الزبون</h4>
                        <Field label="الاسم">{order?.name}</Field>
                        <Field label="الهاتف">{order?.phone}</Field>
                        {order?.lat && order.lat.trim() !== order?.phone?.trim() ? (
                            <Field label="هاتف آخر">{order.lat}</Field>
                        ) : null}
                        <Field label="هاتف الحساب">{order?.user?.phone}</Field>
                        <Field label="المنطقة">{order?.area?.name}</Field>
                        <Field label="العنوان" wide>
                            {order?.address}
                        </Field>
                        <Field label="ملاحظات" wide>
                            {order?.notes}
                        </Field>
                        {order?.user && adminInfo?.role !== "vendor" ? (
                            <Field label="ملاحظة عن الزبون" wide>
                                <UserNote
                                    userId={order.user.id}
                                    note={order.user.note}
                                    userName={order.user.name || order?.name}
                                    editable={canEditNotes}
                                    expand
                                    showMessage={props.showMessage}
                                />
                            </Field>
                        ) : null}
                    </section>

                    <section className="surface-card order-card">
                        <h4 className="order-card__title">المطعم</h4>
                        <Field label="الاسم">{products[0]?.product?.user?.name}</Field>
                        <Field label="الهاتف">{products[0]?.product?.user?.phone}</Field>
                        <Field label="نوع الطلب">
                            {order?.fulfillment_method === "pickup" ? "استلام من المطعم" : "توصيل"}
                        </Field>
                        <Field label="التقدير">
                            {order?.time > 0 ? formatDuration(order.time) : null}
                        </Field>
                        <Field label="الكمية">{order?.total_quantity}</Field>
                    </section>

                    <DriverCard order={order} />
                </div>

                <OrderTimeline order={order} />
                <CustomerTimeline order={order} />

                <section className="order-section">
                    <h4 className="order-card__title">المنتجات المطلوبة</h4>
                    {products.length === 0 ? (
                        /* An order filed by an operator carries no cart lines at
                         * all. The heading used to sit over a 50px void. */
                        <div className="surface-card surface-card--dashed order-empty">
                            لا توجد منتجات مسجّلة على هذا الطلب
                        </div>
                    ) : (
                        <div className="order-products">
                            {products.map((product) => (
                                <div className="surface-card order-product" key={product.id}>
                                    <h6>{product.product.title}</h6>
                                    <Field label="السعر">
                                        {formatMoney(product.product.price)}
                                    </Field>
                                    <Field label="العدد">{product.quantity}</Field>
                                    <Field label="ملاحظات">{product.notes}</Field>
                                    {product.options.length ? (
                                        <div className="order-product__options">
                                            <span className="order-field__label">الإضافات</span>
                                            {product.options.map((addition) => (
                                                <div
                                                    key={addition.id}
                                                    className="order-product__option"
                                                >
                                                    <span>{addition.name}</span>
                                                    <span>{formatMoney(addition.value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Reviews on this order — all three target types in one section
                    so the admin can see the full feedback story without opening
                    each review individually. Not for a user who may not read
                    reviews: the section is absent, not empty. */}
                {canReadReviews ? (
                    <section className="order-section">
                        <h4 className="order-card__title">التقييمات</h4>
                        {reviewsLoading ? (
                            <div style={{ padding: "20px", textAlign: "center" }}>
                                <Ring size={24} lineWeight={4} speed={2} color={BRAND} />
                            </div>
                        ) : orderReviews.length === 0 ? (
                            <div className="surface-card surface-card--dashed order-empty">
                                لا توجد تقييمات على هذا الطلب
                            </div>
                        ) : (
                            <div className="order-reviews">
                                {orderReviews.map((r) => (
                                    <div key={r.id} className="surface-card order-review">
                                        <div className="order-review__head">
                                            <div className="order-review__who">
                                                <OrderStars value={r.rating} />
                                                <strong>{reviewTargetLabel(r)}</strong>
                                                {r.status === "hidden" ? (
                                                    <span className="order-review__hidden">
                                                        (مخفي)
                                                    </span>
                                                ) : null}
                                            </div>
                                            <NavLink to={`/reviews/${r.id}`}>تفاصيل</NavLink>
                                        </div>
                                        {r.comment ? <p>{r.comment}</p> : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                ) : null}
            </div>

            <div className="print-container">
                <Receipt ref={componentRef} order={order} />
            </div>
        </>
    );
};
export default OrderDetails;
